import { BufferGeometry, Float32BufferAttribute, Mesh, Vector3 } from 'three';

import {
  CellGridVolumeErrorCodes,
  VolumeCalculationSourceType,
} from '../../../../../typings/api/skymap/rest/v0/.common';
import { CalculateVolumeParameters } from '../../../../../typings/api/skymap/rest/v0/dynamic-assets';
import { VolumeCalculationMethod } from '../../../react/components/skyview-new/components/volume-calculation-tool/volume-calculation-helper';
import { TokenService } from '../../../services/authentication/token.service';
import { DynamicAssetService } from '../../../services/axios/services/v0/dynamic-asset-service';
import { SharedViewService } from '../../../services/axios/services/v0/shared-view-service';
import { TerrainModelService } from '../../../services/axios/services/v0/terrain-model-service';
import { AssetService } from '../../../services/axios/services/v1/asset-service';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { FileUpload } from '../../../services/network/file-upload';
import { WebApi } from '../../../services/web-api';
import { publishNotification } from '../../../utils/notifications/publish-notification';
import { bufferAttributeUtils } from '../../../utils/three/buffer-attribute';
import { concatMeshes } from '../../../utils/three/mesh';
import { isDefined } from '../../../utils/variables';
import { AssetManager } from '../../asset-manager';
import { CoordinateManager } from '../../coordinate-manager';
import { GltfLoader } from '../../loader/gltf';
import { publish, subscribe, SubscriptionTopic } from '../../messaging/pubsub';
import { ProjectGeodataManager } from '../../project-geodata-manager';
import { clamp, toVector3 } from '../../utilities/math-helpers';
import { Design } from '../design';
import { Dxf } from '../dxf';
import { Geometry } from '../geometry';
import { Glb } from '../glb';
import { TerrainModel } from '../terrain-model';
import { CellGridVolume } from './cell-grid-volume';
import { CellGridVolumeModel } from './model/cell-grid-volume-model';
import { PrismVolumeModel } from './model/prism-volume-model';
import { PrismVolume } from './prism-volume';
import {
  GeometryMesh,
  VolumeBoundaryData,
  VolumeCalculationAsset,
  VolumeModelState,
  VolumeState,
} from './types';

/**
 *  Handler for cell grid volume for an asset.
 */
export class VolumeHandler {
  /**
   *  The volume data for cell grid volume calculations
   */
  get id(): string {
    return this.activeVolume?.id!;
  }

  get name(): string {
    return this.activeVolume?.name!;
  }

  get cut(): number {
    return this.activeVolume?.cut!;
  }
  get fill(): number {
    return this.activeVolume?.fill!;
  }
  get status(): string {
    return this.activeVolume?.status!;
  }

  private _opacity = 1;

  get opacity() {
    return this._opacity;
  }

  set opacity(value: number) {
    this._opacity = clamp(value, 0, 1);

    if (isDefined(this.volumeModel)) {
      this.volumeModel.opacity = this._opacity;
    }
  }

  get errorCode(): CellGridVolumeErrorCodes | undefined {
    return this.activeVolume instanceof CellGridVolume ? this.activeVolume?.error?.code : undefined;
  }

  get errorText() {
    return this.activeVolume?.errorText || '';
  }

  /**
   * Callback that can be invoked to refresh the UI.
   */
  onActiveVolumeChanged?: (newValue?: CellGridVolume | PrismVolume) => void;

  get statusMessage() {
    return this._statusMessage;
  }

  get asset() {
    return this._asset;
  }

  /**
   *  Returns the state of the volume
   */
  get volumeState() {
    return this._volumeState;
  }

  /**
   *  Returns the state of the visible model
   */
  get modelState() {
    return this._modelState;
  }

  protected _statusMessage?: string;

  private _modelState!: VolumeModelState;
  protected _volumeState: VolumeState;

  protected timeoutId?: ReturnType<typeof setTimeout>;
  protected TokenService: TokenService;

  private _activeVolume?: CellGridVolume | PrismVolume;

  get activeVolume(): CellGridVolume | PrismVolume | undefined {
    return this._activeVolume;
  }

  set activeVolume(value: CellGridVolume | PrismVolume | undefined) {
    this.onActiveVolumeChanged?.(value);
    this._activeVolume = value;
  }

  volumeModel?: CellGridVolumeModel | PrismVolumeModel;
  private assetService: AssetService;
  private dynamicAssetService: DynamicAssetService;
  private terrainModelService: TerrainModelService;
  private sharedViewService: SharedViewService;
  protected _asset: VolumeCalculationAsset;
  private pollInterval: number;

  constructor(asset: VolumeCalculationAsset) {
    this._asset = asset;
    this.pollInterval = 5000;

    this.setModelState(VolumeModelState.Hidden);
    this._volumeState = VolumeState.Idle;
    this.TokenService = new TokenService();

    this._volumeState = VolumeState.InProgress;
    this.activeVolume = this._asset.cellGridVolumes[0];
    this.assetService = SkyMapAxiosServiceFactory.instance.createAssetServiceV1();
    this.dynamicAssetService = SkyMapAxiosServiceFactory.instance.createDynamicAssetServiceV0();
    this.terrainModelService = SkyMapAxiosServiceFactory.instance.createTerrainModelServiceV0();
    this.sharedViewService = SkyMapAxiosServiceFactory.instance.createSharedViewServiceV0();

    subscribe(SubscriptionTopic.SkyviewAssetVisibilityChanged, (message) => {
      if (message?.assetUuid === asset?.uuid && isDefined(this.volumeModel)) {
        this.volumeModel.visible = asset.visible && this.isModelVisible();
      }
    });

    this.resolveState();
  }

  setModelState(state: VolumeModelState) {
    this._modelState = state;
    publish(SubscriptionTopic.ModelVolumeStateChanged, {
      state,
      volumeId: this.id,
    });
  }

  resolveState() {
    this.setAssetEnabled(this._asset.cellGridVolumes.length === 0);
    this._statusMessage = this.activeVolume?.statusMessage;

    switch (this.activeVolume && this.activeVolume.status) {
      case 'PENDING':
      case 'RUNNING':
      case 'Pending':
      case 'CalculatingVolume':
      case 'SplittingChunks':
      case 'GeneratingResult':
        this._volumeState = VolumeState.InProgress;
        this.activeVolume!.cut = undefined;
        this.activeVolume!.fill = undefined;
        break;
      case 'COMPLETED':
      case 'Completed':
        this._volumeState = VolumeState.Done;
        break;
      case 'FAILED':
      case 'IncompatibleAssetError':
      case 'IndexLimitExceededError':
      case 'InvalidMeshFormatError':
      case 'NoOverlapError':
      case 'UnknownError':
        this._volumeState = VolumeState.Idle;
        this.activeVolume!.cut = undefined;
        this.activeVolume!.fill = undefined;
        break;
      default:
        this._volumeState = VolumeState.Idle;
        break;
    }
  }

  getStatus(): 'COMPLETED' | 'FAILED' | 'RUNNING' | 'IDLE' {
    switch (this.activeVolume && this.activeVolume.status) {
      case 'PENDING':
      case 'RUNNING':
      case 'Pending':
      case 'CalculatingVolume':
      case 'SplittingChunks':
      case 'GeneratingResult':
        return 'RUNNING';
      case 'COMPLETED':
      case 'Completed':
        return 'COMPLETED';
      case 'FAILED':
      case 'IncompatibleAssetError':
      case 'IndexLimitExceededError':
      case 'InvalidMeshFormatError':
      case 'NoOverlapError':
      case 'UnknownError':
      case 'START_FAILED':
        return 'FAILED';
      default:
        return 'IDLE';
    }
  }

  async refreshVolume(sharedViewId?: string) {
    this.resolveState();

    if (this.volumeModel) {
      const isModelVisible = this.isModelVisible();

      this.clearVolumeModel();
      if (isModelVisible && this._volumeState === VolumeState.Done) {
        await this.toggleModelVisibility(sharedViewId);
      } else {
        this.setModelState(VolumeModelState.Hidden);
      }
    }
  }

  buttonVisible() {
    return this._volumeState !== VolumeState.Done;
  }

  showHideIconVisible() {
    return this._volumeState === VolumeState.Done;
  }

  deleteIconVisible() {
    return this._volumeState === VolumeState.Done;
  }

  isModelVisible() {
    return this.modelState === VolumeModelState.Visible;
  }

  isModelLoading() {
    return this.modelState === VolumeModelState.Loading;
  }

  isModelHidden() {
    return this.modelState === VolumeModelState.Hidden;
  }

  isCutVisible() {
    if (this.volumeModel instanceof PrismVolume) {
      return true;
    }

    // Because the default behavior is to display the whole volume,
    // an unloaded (undefined) volume model has its parts visible
    return this.volumeModel === undefined || this.volumeModel.cutVisibleState;
  }

  isFillVisible() {
    if (this.volumeModel instanceof PrismVolume) {
      return true;
    }

    // Because the default behavior is to display the whole volume,
    // an unloaded (undefined) volume model has its parts visible
    return this.volumeModel === undefined || this.volumeModel.fillVisibleState;
  }

  toggleCut() {
    if (!this.volumeModel) {
      return;
    }
    this.volumeModel.cutVisibleState = !this.volumeModel.cutVisibleState;
  }

  toggleFill() {
    if (!this.volumeModel) {
      return;
    }
    this.volumeModel.fillVisibleState = !this.volumeModel.fillVisibleState;
  }

  isModelRaised() {
    // Because the default behavior is to have the model raised,
    // an unloaded (undefined) volume model has its volume raised
    return this.volumeModel === undefined || this.volumeModel.raised;
  }

  toggleModelRaised() {
    if (!this.volumeModel) {
      return;
    }
    this.volumeModel.raised = !this.volumeModel.raised;
  }

  /**
   *  Adds the volume model to the asset.
   */
  attachModel() {
    if (this.volumeModel) {
      AssetManager.instance.add(this.volumeModel);
    }
  }

  /**
   *  Removes the volume model from the asset.
   */
  detachModel() {
    if (this.volumeModel) {
      AssetManager.instance.add(this.volumeModel);
    }
  }

  setAssetEnabled(state: boolean) {
    const geometry = this._asset as Geometry;
    if (typeof geometry.setEnabled === 'function') {
      geometry.setEnabled(state);
    }
  }

  async calculate(
    volumeName: string,
    targetData?: Mesh[],
    sourceData?: {
      meshes?: Mesh[];
      asset?: VolumeCalculationAsset;
      pointCloudId?: string;
      sourceTerrainModelGeoDataId?: string;
    },
    boundaryData?: VolumeBoundaryData,
    method: VolumeCalculationMethod = 'cell_grid',
  ) {
    const newVolume =
      method === 'cell_grid'
        ? new CellGridVolume({
            id: 'temp-id',
            name: volumeName,
            status: 'PENDING',
            createdAt: new Date().toISOString(),
            sourceAssetId: sourceData?.asset?.assetId,
            sourcePointCloudId: sourceData?.pointCloudId,
            sourceTerrainModelGeoDataId: sourceData?.sourceTerrainModelGeoDataId,
          })
        : new PrismVolume({
            id: 'temp-id',
            name: volumeName,
            status: 'Pending',
            createdAt: new Date().toISOString(),
          });

    if (newVolume instanceof CellGridVolume) {
      this._asset.cellGridVolumes.push(newVolume);
    } else {
      newVolume.sourceName = sourceData?.asset?.name;
      this._asset.prismVolumes.push(newVolume);
    }

    this.activeVolume = newVolume;
    await this.refreshVolume();

    const dynamicAssetCalculation =
      isDefined(sourceData) && isDefined(sourceData.asset) && isDefined(sourceData.meshes);

    try {
      /**
       * The offset to translate the vertices with.
       * For model to model volume calculations we always translate it so that the first
       * vertex will be 0,0,0
       *
       * For calculations towards pointcloud the first vertex should aligh with the position of the pointcloud.
       */
      const vertexOffset = dynamicAssetCalculation
        ? this.getFirstPointOfMesh(targetData)
        : await this.getPointCloudLocalOffsetOrDefault(sourceData?.pointCloudId);

      /**
       * The position of the volume in the projects coordinate system.
       */
      const volumeOffset = toVector3(
        CoordinateManager.getInstance().localToProject(vertexOffset.x, vertexOffset.y),
        CoordinateManager.getInstance().getMAS() + vertexOffset.z,
      );

      const targetGeometryUploadId = await this.extractAndUploadGeometry(targetData, vertexOffset);

      const boundaryGeometryUploadId =
        boundaryData && (await this.extractAndUploadGeometry([boundaryData.mesh], vertexOffset));

      const sourceGeometryUploadId = dynamicAssetCalculation
        ? await this.extractAndUploadGeometry(sourceData.meshes, vertexOffset)
        : undefined;

      const source: {
        id: string;
        type: VolumeCalculationSourceType;
      } =
        sourceData?.asset instanceof TerrainModel && sourceData?.meshes
          ? { id: sourceData.asset.geoDataId, type: 'terrainModel' }
          : sourceData?.asset && sourceData?.meshes
            ? { id: sourceData.asset.assetId!, type: 'dynamicAsset' }
            : { id: sourceData?.pointCloudId!, type: 'pointCloud' };

      const body: CalculateVolumeParameters['body'] = {
        name: volumeName,
        sourceId: source.id,
        volumeCalculationSourceType: source.type,
        targetGeometryUploadId,
        sourceGeometryUploadId,
        offset: volumeOffset,
        boundaryGeometryUploadId,
        method,
      };

      if (this._asset instanceof TerrainModel) {
        newVolume.id = (
          await this.terrainModelService.calculateVolume({
            path: { id: this._asset.geoDataId },
            body,
          })
        ).id;
      } else if (
        this._asset instanceof Geometry ||
        this._asset instanceof Design ||
        this._asset instanceof Dxf ||
        this._asset instanceof Glb
      ) {
        newVolume.id = (
          await this.dynamicAssetService.calculateVolume({
            path: { id: this._asset.assetId! },
            body,
          })
        ).id;
      } else {
        throw new Error('Asset type does not support volume calculation.');
      }

      // This updates the source asset if it is identified.
      // We do this as the initiated volume calculation updates the
      // source asset as well, adding the skyMapVolumeSourceIds to it.
      //
      // This is used to identify source asset name for example.
      const projectId = ProjectGeodataManager.instance().getProject()?.id;
      const sourceAssetRequireRefresh =
        isDefined(projectId) && isDefined(source.id) && source.type === 'dynamicAsset';
      if (sourceAssetRequireRefresh) {
        await AssetManager.instance.refreshDynamicAssetsById(projectId, [source.id]);
      }

      if (newVolume instanceof CellGridVolume) {
        newVolume.status = 'RUNNING';
      }

      // First set the value to undefined to trigger React UI to refresh with the new status
      this.activeVolume = undefined;
      this.activeVolume = newVolume;

      publish(SubscriptionTopic.AssetChanged, this._asset);
      this.initiatePolling(newVolume.id);
    } catch (err) {
      this.handleStartCalculationFailed('err1103');
    }
  }

  private getFirstPointOfMesh(meshes?: Mesh[]) {
    if (isDefined(meshes) && meshes.length > 0 && meshes[0].geometry instanceof BufferGeometry) {
      return meshes[0].localToWorld(
        bufferAttributeUtils.getVector3(
          0,
          meshes[0].geometry.getAttribute('position') as Float32BufferAttribute,
        ),
      );
    }

    return new Vector3(0, 0, 0);
  }

  /**
   *  Calculate the local volume model asset position based on the volume offset value.
   */
  async getOriginalLocalAssetPosition(): Promise<Vector3> {
    // Not used for cell grid volumes.
    return Promise.resolve(new Vector3());
  }

  /**
   *  Toggle the volume model visibility. If it does not exist, load it.
   * @param sharedViewId
   */
  async toggleModelVisibility(
    sharedViewId?: string,
    setLoadingState?: (state: boolean) => void,
  ): Promise<void> {
    if (this.isModelLoading()) {
      return;
    }

    // Not possible to display Prism Volumes in shared view yet.
    if (isDefined(sharedViewId) && this.activeVolume instanceof PrismVolume) {
      this.setModelState(VolumeModelState.Hidden);
      setLoadingState?.(false);
      return;
    }

    try {
      this.setModelState(VolumeModelState.Loading);

      if (!this.volumeModel) {
        setLoadingState?.(true);

        if (this.activeVolume instanceof PrismVolume) {
          const response = await this.assetService.getPrismVolumeUrl({
            path: {
              dynamicAssetId: this.asset.assetId!,
              prismVolumeId: this.id,
            },
          });

          const arrayBuffer = await WebApi.fetchS3DataAsArrayBuffer(response.data.url);
          const glb = await GltfLoader.instance.loadGltf(Buffer.from(arrayBuffer));

          this.volumeModel = new PrismVolumeModel(this.id, glb);

          this.volumeModel.refreshLocalPosition();

          AssetManager.instance.add(this.volumeModel);
          this.volumeModel.visible = true;
        } else if (this.activeVolume instanceof CellGridVolume) {
          const modelUrls =
            sharedViewId && this.id
              ? await this.sharedViewService.fetchVolumeModelUrls({
                  path: { id: sharedViewId, volumeId: this.id },
                })
              : this._asset instanceof TerrainModel
                ? await this.terrainModelService.fetchVolumeModelUrls({
                    path: { id: this._asset.geoDataId, volumeId: this.id },
                  })
                : await this.dynamicAssetService.fetchVolumeModelUrls({
                    path: { assetId: this.asset.assetId!, volumeId: this.id },
                  });
          if (!(modelUrls?.urls.length > 0)) {
            this.setModelState(VolumeModelState.Hidden);

            return publishNotification('err1105');
          }

          this.volumeModel = new CellGridVolumeModel(
            this.id,
            this.activeVolume?.cellWidth,
            this.activeVolume?.cellDepth,
          );

          this.volumeModel.projectPosition = this.activeVolume.offset!.clone();
          this.volumeModel.refreshLocalPosition();

          AssetManager.instance.add(this.volumeModel);

          /**
           *  The volume chunks are loaded synchronously.
           *  Hide the volume mesh until the whole mesh is loaded.
           */
          this.volumeModel.visible = false;
          await this.volumeModel.loadVolumeModel(modelUrls.urls);
          this.volumeModel.visible = true;
        }

        this.setModelState(VolumeModelState.Visible);
      } else {
        this.volumeModel.visible = !this.volumeModel.visible;
        if (this.volumeModel.visible) {
          this.setModelState(VolumeModelState.Visible);
        } else {
          this.setModelState(VolumeModelState.Hidden);
        }
      }
    } catch (err) {
      publishNotification('err1112');
    } finally {
      setLoadingState?.(false);
    }
  }

  handleStartCalculationFailed(notificationErrorCode: string) {
    if (this.activeVolume) {
      this.activeVolume.status = 'START_FAILED';
    }

    this._volumeState = VolumeState.Idle;
    this._statusMessage = this.activeVolume?.statusMessage;
    this.setAssetEnabled(true);
    publishNotification(notificationErrorCode);
  }

  /**
   * Clears and disposes the loaded volume model
   */
  clearVolumeModel() {
    if (this.volumeModel) {
      AssetManager.instance.remove(this.volumeModel);
      this.volumeModel.dispose();
      this.volumeModel = undefined;
    }
  }

  /**
   *  Deletes the cell grid volume of the asset, and enables the asset for editing
   */
  async delete() {
    if (this.activeVolume instanceof PrismVolume) {
      await this.assetService.deletePrismVolume({
        path: {
          dynamicAssetId: this.asset.assetId!,
          prismVolumeId: this.id,
        },
      });
    } else if (this._asset instanceof TerrainModel) {
      await this.terrainModelService.deleteVolumeCalculation({
        path: { id: this._asset.geoDataId, volumeId: this.id },
      });
    } else {
      await this.dynamicAssetService.deleteOneGeometryVolumeCellGrid({
        path: { assetId: this.asset.assetId!, volumeId: this.id },
      });
    }

    this.abortPolling();
    this.clearVolumeModel();
    this.setModelState(VolumeModelState.Hidden);

    if (this.activeVolume) {
      if (this.activeVolume instanceof PrismVolume) {
        const index = this._asset.prismVolumes.indexOf(this.activeVolume);
        this._asset.prismVolumes.splice(index, 1);
        // Re-assign to a new array so listeners refreshing the asset list detect the change.
        this._asset.prismVolumes = [...this._asset.prismVolumes];
      } else {
        const index = this._asset.cellGridVolumes.indexOf(this.activeVolume);
        this._asset.cellGridVolumes.splice(index, 1);
        // Re-assign to a new array so listeners refreshing the asset list detect the change.
        this._asset.cellGridVolumes = [...this._asset.cellGridVolumes];
      }
    }

    this.activeVolume = undefined;
    this.resolveState();

    publish(SubscriptionTopic.AssetChanged, this.asset);
  }

  async getPointCloudLocalOffsetOrDefault(pointCloudId?: string): Promise<Vector3> {
    if (!isDefined(pointCloudId)) {
      return new Vector3(0, 0, 0);
    }

    const pointCloud = await SkyMapAxiosServiceFactory.instance
      .createPointCloudServiceV0()
      .fetchById({
        path: {
          id: pointCloudId,
        },
      });

    return new Vector3(...pointCloud.data.origo).sub(
      CoordinateManager.getInstance().getProjectionOrigo()!,
    );
  }

  /**
   *  Returns the upload id after extracting the geometry information from a mesh and upload the data.
   * @param meshes
   * @param offset The offset will be subtracted from the resulting vertices.
   */
  private async extractAndUploadGeometry(meshes: Mesh[] = [], offset = new Vector3()) {
    const meshData =
      meshes.length === 0
        ? (this._asset as Geometry).extractMeshInformation()
        : concatMeshes(meshes, true);

    /**
     *  Because cesium point cloud is used in other scenes,
     *  we compensate for the relative offset position between the origo of the point cloud and active scene
     */
    for (let i = 0; i < meshData.vertices.length; i += 3) {
      meshData.vertices[i] -= offset.x;
      meshData.vertices[i + 1] -= offset.y;
      meshData.vertices[i + 2] -= offset.z;
    }

    return await this.uploadGeometryMesh(meshData);
  }

  /**
   *  Handles polling of processing volume calculations
   * @param id
   */
  initiatePolling(id: string) {
    if (this.timeoutId !== undefined) {
      return;
    }

    const executePoll = async () => {
      const volume =
        this._asset.cellGridVolumes.find((vol) => vol.id === id) ??
        this._asset.prismVolumes.find((vol) => vol.id === id);
      if (!volume || !volume.isProcessing()) {
        this.timeoutId = undefined;
        return;
      }

      if (this._asset instanceof TerrainModel) {
        await AssetManager.instance.updateTerrainModel(this._asset.geoDataId);
      } else if (isDefined(this._asset.assetId)) {
        await AssetManager.instance.updateAsset(this._asset.assetId, 'DynamicAsset');
      }

      publish(SubscriptionTopic.AssetChanged, this._asset);

      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      this.timeoutId = setTimeout(executePoll, this.pollInterval);
    };

    // If timeoutId  is set, polling is already underway
    if (!this.timeoutId) {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      this.timeoutId = setTimeout(executePoll, 0);
    }
  }

  /**
   *  Abort ongoing polling
   */
  protected abortPolling() {
    // If timeoutId  is set, abort timeout
    if (!this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
  }

  /**
   *  Upload the geometry to the S3 to circumvent AWS API Gateway & Lambda payload limit.
   * @param geometry
   */
  async uploadGeometryMesh(geometry: GeometryMesh): Promise<string> {
    const geometryData = new Float32Array([
      geometry.vertices.length,
      geometry.indices.length,
      ...geometry.vertices,
      ...geometry.indices,
    ]);

    const upload = new FileUpload({
      apiBaseUrl: WebApi.getUrl(),
      authToken: this.TokenService.get(),
      file: new File([geometryData.buffer], 'mesh'), // create data from blob?
      concurrency: 1,
    });

    return (await upload.start()).id;
  }
}
